/** Bottom drawer listing debug-flagged API requests (devtools-style).
 *
 * Rows come from the module-level store fed by the axios interceptors
 * (src/api/debugMode.ts): transport metadata is always shown; the expandable
 * detail renders the backend `_debug` summary — per-alias query counts,
 * `debug_log()` messages, and the SQL table sorted by duration — when the
 * backend attached one (requires the `console_debug` permission server-side).
 */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import type { DebugRequestEntry, DebugSummary } from '@/api/debugMode';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { formatMs, StatusCodeChip } from '@/features/api-requests/apiRequestFormat';

import { downloadHar } from './har';
import { useDebugMode } from './useDebugMode';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

export function DebugDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { enabled, setEnabled, entries, clear } = useDebugMode();
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ [`& .MuiDrawer-paper`]: { maxHeight: '60vh' } }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              color="warning"
            />
          }
          label={
            <Typography variant="body2">
              Debug mode — attach <Box component="code" sx={mono}>X-CP-Debug</Box> to every API
              request
            </Typography>
          }
        />
        <Typography variant="body2" color="text.secondary">
          {entries.length} request{entries.length === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Download the captured requests as a HAR file (response bodies included, credentials redacted)">
          <span>
            <Button
              size="small"
              onClick={() => downloadHar(entries)}
              disabled={entries.length === 0}
            >
              Export HAR
            </Button>
          </span>
        </Tooltip>
        <Button size="small" onClick={clear} disabled={entries.length === 0}>
          Clear
        </Button>
        <HelpLink story="ui-debug-toolbar" newTab />
        <IconButton size="small" onClick={onClose} aria-label="Close debug toolbar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box sx={{ overflow: 'auto' }}>
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            {enabled
              ? 'Recording — use the app and every API request will appear here with its backend SQL/timing summary.'
              : 'Turn the switch on, then use the app — requests will appear here with their backend SQL/timing summaries.'}
          </Typography>
        ) : (
          entries.map((entry) => <DebugEntryRow key={entry.id} entry={entry} />)
        )}
      </Box>
    </Drawer>
  );
}

function DebugEntryRow({ entry }: { entry: DebugRequestEntry }) {
  const s = entry.summary;
  return (
    <Accordion disableGutters square slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' } }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: '100%', minWidth: 0, pr: 1 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {new Date(entry.ts).toLocaleTimeString()}
          </Typography>
          <Chip size="small" variant="outlined" label={entry.method} sx={{ ...mono, height: 22 }} />
          <Tooltip title={entry.query ? `${entry.path}?${entry.query}` : entry.path}>
            <Link
              href={entry.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              // Don't toggle the accordion when the link is clicked/copied.
              onClick={(e) => e.stopPropagation()}
              sx={{
                ...mono,
                flexGrow: 1,
                flexShrink: 1,
                minWidth: 0,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.primary',
              }}
            >
              {entry.path}
              {entry.query ? (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  ?{entry.query}
                </Box>
              ) : null}
            </Link>
          </Tooltip>
          <StatusCodeChip code={entry.status} />
          <Typography variant="body2" sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            {formatMs(entry.durationMs)}
          </Typography>
          {s ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
            >
              db {formatMs(s.db_ms)} · {s.query_count}q
            </Typography>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <RequestIdRow requestId={entry.requestId} />
        {s ? (
          <SummaryDetail summary={s} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No debug summary returned — the backend attaches one only when your user holds the{' '}
            <Box component="code" sx={mono}>console_debug</Box> permission.
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function RequestIdRow({ requestId }: { requestId: string | null }) {
  const snackbar = useSnackbar();
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Request ID
      </Typography>
      {requestId ? (
        <>
          <Typography component="span" sx={mono}>
            {requestId}
          </Typography>
          <Tooltip title="Copy request ID">
            <IconButton
              size="small"
              aria-label="Copy request ID"
              onClick={() => {
                void navigator.clipboard.writeText(requestId);
                snackbar.show('Request ID copied.', 'success');
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      )}
    </Stack>
  );
}

function SummaryDetail({ summary }: { summary: DebugSummary }) {
  const queries = useMemo(
    () => [...(summary.queries ?? [])].sort((a, b) => b.ms - a.ms),
    [summary.queries],
  );
  const aliases = Object.entries(summary.queries_by_alias ?? {});
  const messages = summary.messages ?? [];
  return (
    <Stack spacing={1.5}>
      {summary.truncated ? (
        <Alert severity="warning" sx={{ py: 0 }}>
          Capture was truncated — query/message caps were hit; totals still count dropped queries.
        </Alert>
      ) : null}
      {aliases.length > 0 ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {aliases.map(([alias, agg]) => (
            <Chip
              key={alias}
              size="small"
              variant="outlined"
              label={`${alias}: ${agg.count}q · ${formatMs(agg.ms)}`}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            />
          ))}
        </Stack>
      ) : null}
      {messages.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Messages
          </Typography>
          {messages.map((m, i) => (
            <Typography key={i} sx={{ ...mono, fontSize: 12 }}>
              +{m.offset_ms}ms {m.msg}{' '}
              {Object.keys(m.data).length > 0 ? JSON.stringify(m.data) : ''}
            </Typography>
          ))}
        </Box>
      ) : null}
      {queries.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Queries (slowest first)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}>#</TableCell>
                <TableCell sx={{ width: 120 }}>Alias</TableCell>
                <TableCell sx={{ width: 90 }} align="right">
                  Time
                </TableCell>
                <TableCell sx={{ width: 90 }} align="right">
                  Offset
                </TableCell>
                <TableCell>SQL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queries.map((q) => (
                <TableRow key={q.i} hover>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{q.i}</TableCell>
                  <TableCell sx={{ ...mono, fontSize: 12 }}>{q.alias}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatMs(q.ms)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    +{formatMs(q.offset_ms)}
                  </TableCell>
                  <TableCell>
                    <Box
                      component="pre"
                      sx={{
                        ...mono,
                        fontSize: 12,
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {q.sql}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Stack>
  );
}
